import { Injectable } from '@angular/core';
import { Badge } from '@capawesome/capacitor-badge';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

@Injectable({
  providedIn: 'root'
})
export class BadgeService {
  private readonly TIMEOUT_MS = 2000; // 2 second timeout for badge operations

  constructor() {}

  /**
   * Clear the app badge count with timeout protection
   * This method will never block or throw errors - safe to call without await
   */
  async clearBadge(): Promise<void> {
    // Only clear badge on native platforms (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      // Use Promise.race to add timeout protection
      await Promise.race([
        Badge.clear(),
        this.timeout(this.TIMEOUT_MS, 'clearBadge')
      ]);

      // Also clear all notifications from the notification tray (iOS & Android)
      // On Android: This removes notification icons that can cause badge to persist
      // On iOS: This clears notifications from Notification Center
      await Promise.race([
        PushNotifications.removeAllDeliveredNotifications(),
        this.timeout(this.TIMEOUT_MS, 'clearNotifications')
      ]);

    } catch (error) {
      // Silent fail - don't block app functionality
      console.warn('Failed to clear badge/notifications (non-critical):', error);
    }
  }

  /**
   * Clear badge without awaiting - fire and forget
   * Use this when you don't want to block page loading
   */
  clearBadgeAsync(): void {
    this.clearBadge().catch(() => {
      // Silently catch any errors
    });
  }

  /**
   * Timeout helper
   */
  private timeout(ms: number, operation: string): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${operation} timed out after ${ms}ms`)), ms);
    });
  }

  /**
   * Set badge count to a specific number
   * @param count The badge count to set
   */
  async setBadgeCount(count: number): Promise<void> {
    // Only set badge on native platforms (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await Badge.set({ count });
      
    } catch (error) {
      console.error('Error setting badge count:', error);
    }
  }

  /**
   * Get current badge count
   */
  async getBadgeCount(): Promise<number> {
    // Only get badge on native platforms (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      return 0;
    }

    try {
      const result = await Badge.get();
      return result.count;
    } catch (error) {
      console.error('Error getting badge count:', error);
      return 0;
    }
  }

  /**
   * Check if badge is supported on this platform
   */
  async isSupported(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const result = await Badge.isSupported();
      return result.isSupported;
    } catch (error) {
      console.error('Error checking badge support:', error);
      return false;
    } 
  }

  /**
   * Request permission to set badges (iOS only)
   */
  async requestPermission(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await Badge.requestPermissions();
      
    } catch (error) {
      console.error('Error requesting badge permissions:', error);
    }
  }

  /**
   * Check if badge permissions are granted (iOS only)
   */
  async checkPermissions(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const result = await Badge.checkPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.error('Error checking badge permissions:', error);
      return false;
    }
  }
}
